/**
 * Sunum biçimlendiricileri — yalnızca GÖSTERİM için.
 *
 * PARA (ADR-03): bu dosya para biçimlendirmez. Para her yerde <Tutar>
 * bileşeniyle gösterilir; buradaki yardımcılar ZAMAN, SAYAÇ ve DURUM
 * etiketleri içindir. Number()/parseFloat()/unary + kullanılmaz.
 */
import {
  MutabakatSebebi,
  OdemeTipi,
  SatisDurumu,
  TerminalDurumu,
} from "@kantin/shared";

// ---------------------------------------------------------------------------
// Zaman
// ---------------------------------------------------------------------------

export function tarihSaat(deger: string | Date | null | undefined): string {
  if (!deger) return "—";
  const d = typeof deger === "string" ? new Date(deger) : deger;
  if (Number.isNaN(d.getTime())) return String(deger);
  return d.toLocaleString("tr-TR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function tarih(deger: string | Date | null | undefined): string {
  if (!deger) return "—";
  const d = typeof deger === "string" ? new Date(deger) : deger;
  if (Number.isNaN(d.getTime())) return String(deger);
  return d.toLocaleDateString("tr-TR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

export function saat(deger: string | Date | null | undefined): string {
  if (!deger) return "—";
  const d = typeof deger === "string" ? new Date(deger) : deger;
  if (Number.isNaN(d.getTime())) return String(deger);
  return d.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" });
}

/** Verilen andan bu yana geçen süre, milisaniye. Geçersiz tarihte null. */
export function gecenMs(
  deger: string | Date | null | undefined,
): number | null {
  if (!deger) return null;
  const d = typeof deger === "string" ? new Date(deger) : deger;
  const t = d.getTime();
  if (Number.isNaN(t)) return null;
  return Date.now() - t;
}

/**
 * "12 dk", "3 sa 20 dk", "2 gün 4 sa" — mutabakat YAŞLANMASI ve heartbeat
 * tazeliği için. Yaşlanma bir karar girdisidir: "3 gündür bekliyor" ile
 * "3 dakikadır bekliyor" aynı ekranda aynı görünmemelidir.
 */
export function sureMetni(ms: number | null): string {
  if (ms === null) return "—";
  if (ms < 0) return "az önce";
  const dakika = Math.floor(ms / 60_000);
  if (dakika < 1) return "az önce";
  if (dakika < 60) return `${dakika} dk`;
  const saatler = Math.floor(dakika / 60);
  if (saatler < 24) {
    const kalanDk = dakika - saatler * 60;
    return kalanDk === 0 ? `${saatler} sa` : `${saatler} sa ${kalanDk} dk`;
  }
  const gunler = Math.floor(saatler / 24);
  const kalanSa = saatler - gunler * 24;
  return kalanSa === 0 ? `${gunler} gün` : `${gunler} gün ${kalanSa} sa`;
}

export function yaslanma(deger: string | Date | null | undefined): string {
  return sureMetni(gecenMs(deger));
}

/**
 * "2026-08-09" -> "09.08.2026" — sunucudan gelen TAKVİM GÜNÜ string'i için.
 *
 * Date'e çevrilmez: `gun` zaten okul saat diliminde hesaplanmış bir takvim
 * günüdür; araya ikinci bir saat dilimi dönüşümü sokmak günü kaydırırdı.
 * Zaman damgaları için tarih()/tarihSaat() kullanılır.
 */
export function gunGoster(gun: string | null | undefined): string {
  if (!gun) return "—";
  const [y, a, g] = gun.split("-");
  if (!y || !a || !g) return gun;
  return `${g}.${a}.${y}`;
}

/** "YYYY-MM-DD" gününün ayın ilk günü — rapor aralıklarının varsayılanı. */
export function ayinIlkGunu(gun: string): string {
  return `${gun.slice(0, 7)}-01`;
}

/** Tarayıcı yerelinde bugün ("YYYY-MM-DD"). Okul günü için ozet.gun tercih edilir. */
export function yerelBugun(): string {
  return new Date().toLocaleDateString("en-CA");
}

// ---------------------------------------------------------------------------
// Kod -> Türkçe etiket
// ---------------------------------------------------------------------------

export const SEBEP_ADI: Record<number, string> = {
  [MutabakatSebebi.SUNUCU_REDDI]: "Sunucu reddi",
  [MutabakatSebebi.CAPRAZ_LIMIT_ASIMI]: "Çapraz limit aşımı",
  [MutabakatSebebi.TRANSFER_FARKI]: "Transfer farkı",
  [MutabakatSebebi.BAKIYE_SAPMASI]: "Bakiye sapması",
};

export const SATIS_DURUM_ADI: Record<number, string> = {
  [SatisDurumu.TAMAM]: "Tamam",
  [SatisDurumu.IPTAL]: "İptal",
  [SatisDurumu.MUTABAKAT_BEKLIYOR]: "Mutabakat bekliyor",
};

export const ODEME_TIPI_ADI: Record<number, string> = {
  [OdemeTipi.KART]: "Kart bakiyesi",
  [OdemeTipi.NAKIT]: "Nakit",
  [OdemeTipi.CARI]: "Cari",
};

export const TERMINAL_DURUM_ADI: Record<number, string> = {
  [TerminalDurumu.AKTIF]: "Aktif",
  [TerminalDurumu.ASKIDA]: "Askıda",
  [TerminalDurumu.IPTAL]: "İptal",
};

/** anomali.kural — worker'daki AnomaliKurali ile birebir (isler/anomali.ts). */
export const ANOMALI_KURAL_ADI: Record<string, string> = {
  ayni_nokta_iki_terminal: "Aynı noktada iki terminal",
  harcama_sicramasi: "Harcama sıçraması",
  ardisik_iptal: "Ardışık iptal",
  cok_noktali_kullanim: "Çok noktalı kullanım",
};

export const SIDDET_ADI: Record<number, string> = {
  1: "Zayıf",
  2: "Orta",
  3: "Güçlü",
};

export const AKTOR_TIPI_ADI: Record<string, string> = {
  personel: "Personel",
  veli: "Veli",
  terminal: "Terminal",
  sistem: "Sistem",
};

export function kisiAdi(
  kisi: {
    ad: string | null;
    soyad: string | null;
    sinif: string | null;
  } | null,
): string {
  if (!kisi) return "—";
  const tam = [kisi.ad, kisi.soyad].filter(Boolean).join(" ").trim();
  if (!tam) return "—";
  return kisi.sinif ? `${tam} (${kisi.sinif})` : tam;
}

/** Anomali/audit detayı serbest JSON'dur; okunabilir bir özet çıkarılır. */
export function detayAciklamasi(detay: unknown): string | null {
  if (detay && typeof detay === "object" && "aciklama" in detay) {
    const a = (detay as { aciklama?: unknown }).aciklama;
    if (typeof a === "string") return a;
  }
  return null;
}

export function jsonGoster(deger: unknown): string {
  if (deger === null || deger === undefined) return "—";
  try {
    return JSON.stringify(deger, null, 2);
  } catch {
    return String(deger);
  }
}
